import {
  ApiMarket,
  ApiMarketPrice,
  ApiCycle,
  ApiOrder,
  ApiOrderSettlement,
  ApiLocker,
  ApiLockerOrder,
  BetSignatureResponse,
  TransferMessageHashResponse,
  ApiTvl,
  ApiTvlHistory,
  ApiPriceHistory,
  ApiTokenInfo,
  Address,
} from './api.types';

// ============================================================================
// HELPERS
// ============================================================================

const HOUR = 3600_000;
const DAY = 24 * HOUR;
const now = Date.now();

/** Generate a fake 66-char tx hash */
function txHash(seed: number): string {
  return '0x' + seed.toString(16).padStart(64, '0');
}

// ============================================================================
// MOCK USERS
// ============================================================================

export const MOCK_USERS: Address[] = [
  '0xaf5cb9e868e0c1664c6e480a9907a4ea0c92a833' as Address,
  '0x923b7e9fa7118a10669b7be43087fc2f7dd53927' as Address,
  '0x1a2b3c4d5e6f708192a3b4c5d6e7f80910111213' as Address,
  '0xdeadbeefdeadbeefdeadbeefdeadbeefdeadbeef' as Address,
  '0xcafebabecafebabecafebabecafebabecafebabe' as Address,
];

// ============================================================================
// MARKETS — 6 markets
// ============================================================================

export const MOCK_API_MARKETS: ApiMarket[] = [
  {
    chain_id: 11155111,
    market_id: 'CTS-BTC',
    prediction_contract: '0x68f966e387610d1d2b5a9a84806219f3e0b9155f' as Address,
    start_block: 10410000,
    status: 'active',
    base_token_decimals: 18,
    config_json: {
      feeRate: 0,
      rewardRate: 50000,
      price_source: 'chainlink',
      chainlink_feed_id: '0x00037da06d56d083fe599397a4769a042d63aa73dc4ef57709d31e9971a5b439',
    },
    icon_url: 'assets/icons/btc.svg',
    token_symbol: 'BTC',
    title: 'Will BTC be above $100,000 on Apr 1, 2026?',
    description: 'Bitcoin price prediction market',
  },
  {
    chain_id: 11155111,
    market_id: 'CTS-ETH',
    prediction_contract: '0x2c2582ba7b7830474f1990bbb59c6ac57a59b20e' as Address,
    start_block: 10410000,
    status: 'active',
    base_token_decimals: 18,
    config_json: {
      feeRate: 0,
      rewardRate: 50000,
      price_source: 'chainlink',
      chainlink_feed_id: '0x00038c2628907d2c35b5dcc7a7d2ad143580261b1b56dcb535828d8e32ee58c6',
    },
    icon_url: 'assets/icons/eth.svg',
    token_symbol: 'ETH',
    title: 'Will ETH be above $4,000 on Apr 1, 2026?',
    description: 'Ethereum price prediction market',
  },
  {
    chain_id: 11155111,
    market_id: 'CTS-BNB',
    prediction_contract: '0x0000000000000000000000000000000000000003' as Address,
    start_block: 10430000,
    status: 'active',
    base_token_decimals: 18,
    config_json: {
      feeRate: 5000,
      rewardRate: 25000,
      price_source: 'binance',
      quote_pair: 'BNB-USDT',
    },
    icon_url: null,
    token_symbol: 'BNB',
    title: 'Will BNB be above $600 on Apr 1, 2026?',
    description: 'BNB price prediction market',
  },
  {
    chain_id: 11155111,
    market_id: 'CTS-SOL',
    prediction_contract: '0x0000000000000000000000000000000000000004' as Address,
    start_block: 10440000,
    status: 'active',
    base_token_decimals: 18,
    config_json: {
      feeRate: 3000,
      rewardRate: 40000,
      price_source: 'chainlink',
      chainlink_feed_id: '0x00039d9e45394f473ab1f050a1b963e6b05351e52d71e507509ada0c95ed75b8',
    },
    icon_url: null,
    token_symbol: 'SOL',
    title: 'Will SOL be above $200 on Apr 1, 2026?',
    description: 'Solana price prediction market',
  },
  {
    chain_id: 11155111,
    market_id: 'CTS-DOGE',
    prediction_contract: '0x0000000000000000000000000000000000000005' as Address,
    start_block: 10450000,
    status: 'active',
    base_token_decimals: 18,
    config_json: {
      feeRate: 5000,
      rewardRate: 30000,
      price_source: 'binance',
      quote_pair: 'DOGE-USDT',
    },
    icon_url: null,
    token_symbol: 'DOGE',
    title: 'Will DOGE be above $0.50 on Apr 1, 2026?',
    description: 'Dogecoin price prediction market',
  },
  {
    chain_id: 11155111,
    market_id: 'CTS-AVAX',
    prediction_contract: '0x0000000000000000000000000000000000000006' as Address,
    start_block: 10460000,
    status: 'active',
    base_token_decimals: 18,
    config_json: {
      feeRate: 4000,
      rewardRate: 35000,
      price_source: 'chainlink',
      chainlink_feed_id: '0x00041a9e75fd2cb90ac38c62a79b5d85eabc8d154c03b4d7c3f15a6b0e1d2f8a',
    },
    icon_url: null,
    token_symbol: 'AVAX',
    title: 'Will AVAX be above $50 on Apr 1, 2026?',
    description: 'Avalanche price prediction market',
  },
];

// ============================================================================
// CYCLES — across 6 markets
// ============================================================================

export const MOCK_API_CYCLES: ApiCycle[] = [
  // CTS-BTC: 5 cycles (2 settled, 1 betting_ended, 1 active, 1 scheduled)
  {
    id: 1, chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 0,
    start_time: new Date(now - 48 * HOUR).toISOString(),
    betting_end_time: new Date(now - 46 * HOUR).toISOString(),
    end_time: new Date(now - 44 * HOUR).toISOString(),
    state: 'SETTLED',
    create_tx_hash: txHash(0x1001), settle_tx_hash: txHash(0x1002),
    side_up_total_raw: '22000000000000000000000', side_down_total_raw: '18000000000000000000000',
    start_price: '97200000000000000000000', result: 1, final_price: '99100000000000000000000',
    prize_amount: '38000000000000000000000', resolved_at: new Date(now - 44 * HOUR).toISOString(),
    shares_up_total_raw: '22000000000000000000000', shares_down_total_raw: '18000000000000000000000',
    shares_up_total: '22000', shares_down_total: '18000',
    side_up_total: '22000', side_down_total: '18000', base_token_decimals: 18,
  },
  {
    id: 2, chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 1,
    start_time: new Date(now - 24 * HOUR).toISOString(),
    betting_end_time: new Date(now - 22 * HOUR).toISOString(),
    end_time: new Date(now - 20 * HOUR).toISOString(),
    state: 'SETTLED',
    create_tx_hash: txHash(0x2001), settle_tx_hash: txHash(0x2002),
    side_up_total_raw: '8500000000000000000000', side_down_total_raw: '12000000000000000000000',
    start_price: '98500000000000000000000', result: 2, final_price: '97800000000000000000000',
    prize_amount: '19475000000000000000000', resolved_at: new Date(now - 20 * HOUR).toISOString(),
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '8500', side_down_total: '12000', base_token_decimals: 18,
  },
  {
    id: 3, chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 2,
    start_time: new Date(now - 8 * HOUR).toISOString(),
    betting_end_time: new Date(now - 2 * HOUR).toISOString(),
    end_time: new Date(now + 1 * HOUR).toISOString(),
    state: 'BETTING_ENDED',
    create_tx_hash: txHash(0x3001), settle_tx_hash: null,
    side_up_total_raw: '30000000000000000000000', side_down_total_raw: '25000000000000000000000',
    start_price: '99500000000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '30000', side_down_total: '25000', base_token_decimals: 18,
  },
  {
    id: 4, chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 3,
    start_time: new Date(now - 1 * HOUR).toISOString(),
    betting_end_time: new Date(now + 2 * HOUR).toISOString(),
    end_time: new Date(now + 4 * HOUR).toISOString(),
    state: 'ACTIVE',
    create_tx_hash: txHash(0x4001), settle_tx_hash: null,
    side_up_total_raw: '15000000000000000000000', side_down_total_raw: '8500000000000000000000',
    start_price: '100200000000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '15000', side_down_total: '8500', base_token_decimals: 18,
  },
  {
    id: 5, chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 4,
    start_time: new Date(now + 4 * HOUR).toISOString(),
    betting_end_time: new Date(now + 7 * HOUR).toISOString(),
    end_time: new Date(now + 9 * HOUR).toISOString(),
    state: 'SCHEDULED',
    create_tx_hash: null, settle_tx_hash: null,
    side_up_total_raw: '0', side_down_total_raw: '0',
    start_price: null, result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '0', side_down_total: '0', base_token_decimals: 18,
  },
  // CTS-ETH: 4 cycles (1 settled, 1 betting_ended, 1 active, 1 scheduled)
  {
    id: 6, chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 0,
    start_time: new Date(now - 36 * HOUR).toISOString(),
    betting_end_time: new Date(now - 34 * HOUR).toISOString(),
    end_time: new Date(now - 32 * HOUR).toISOString(),
    state: 'SETTLED',
    create_tx_hash: txHash(0x6001), settle_tx_hash: txHash(0x6002),
    side_up_total_raw: '5000000000000000000000', side_down_total_raw: '7000000000000000000000',
    start_price: '3500000000000000000000', result: 2, final_price: '3420000000000000000000',
    prize_amount: '11400000000000000000000', resolved_at: new Date(now - 32 * HOUR).toISOString(),
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '5000', side_down_total: '7000', base_token_decimals: 18,
  },
  {
    id: 7, chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 1,
    start_time: new Date(now - 12 * HOUR).toISOString(),
    betting_end_time: new Date(now - 4 * HOUR).toISOString(),
    end_time: new Date(now + 0.5 * HOUR).toISOString(),
    state: 'BETTING_ENDED',
    create_tx_hash: txHash(0x7001), settle_tx_hash: null,
    side_up_total_raw: '12000000000000000000000', side_down_total_raw: '8500000000000000000000',
    start_price: '3480000000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '12000', side_down_total: '8500', base_token_decimals: 18,
  },
  {
    id: 8, chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 2,
    start_time: new Date(now - 1 * HOUR).toISOString(),
    betting_end_time: new Date(now + 2 * HOUR).toISOString(),
    end_time: new Date(now + 4 * HOUR).toISOString(),
    state: 'ACTIVE',
    create_tx_hash: txHash(0x8001), settle_tx_hash: null,
    side_up_total_raw: '9000000000000000000000', side_down_total_raw: '6000000000000000000000',
    start_price: '3580000000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '9000', side_down_total: '6000', base_token_decimals: 18,
  },
  {
    id: 9, chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 3,
    start_time: new Date(now + 4 * HOUR).toISOString(),
    betting_end_time: new Date(now + 7 * HOUR).toISOString(),
    end_time: new Date(now + 9 * HOUR).toISOString(),
    state: 'SCHEDULED',
    create_tx_hash: null, settle_tx_hash: null,
    side_up_total_raw: '0', side_down_total_raw: '0',
    start_price: null, result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '0', side_down_total: '0', base_token_decimals: 18,
  },
  // CTS-BNB: 4 cycles (1 settled, 1 betting_ended, 1 active, 1 scheduled)
  {
    id: 10, chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 0,
    start_time: new Date(now - 40 * HOUR).toISOString(),
    betting_end_time: new Date(now - 38 * HOUR).toISOString(),
    end_time: new Date(now - 36 * HOUR).toISOString(),
    state: 'SETTLED',
    create_tx_hash: txHash(0x9001), settle_tx_hash: txHash(0x9002),
    side_up_total_raw: '6000000000000000000000', side_down_total_raw: '4500000000000000000000',
    start_price: '620000000000000000000', result: 1, final_price: '645000000000000000000',
    prize_amount: '9975000000000000000000', resolved_at: new Date(now - 36 * HOUR).toISOString(),
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '6000', side_down_total: '4500', base_token_decimals: 18,
  },
  {
    id: 11, chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 1,
    start_time: new Date(now - 10 * HOUR).toISOString(),
    betting_end_time: new Date(now - 3 * HOUR).toISOString(),
    end_time: new Date(now + 0.5 * HOUR).toISOString(),
    state: 'BETTING_ENDED',
    create_tx_hash: txHash(0x9101), settle_tx_hash: null,
    side_up_total_raw: '7500000000000000000000', side_down_total_raw: '5500000000000000000000',
    start_price: '635000000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '7500', side_down_total: '5500', base_token_decimals: 18,
  },
  {
    id: 12, chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 2,
    start_time: new Date(now - 2 * HOUR).toISOString(),
    betting_end_time: new Date(now + 1 * HOUR).toISOString(),
    end_time: new Date(now + 3 * HOUR).toISOString(),
    state: 'ACTIVE',
    create_tx_hash: txHash(0x9201), settle_tx_hash: null,
    side_up_total_raw: '4000000000000000000000', side_down_total_raw: '3500000000000000000000',
    start_price: '680000000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '4000', side_down_total: '3500', base_token_decimals: 18,
  },
  {
    id: 13, chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 3,
    start_time: new Date(now + 3 * HOUR).toISOString(),
    betting_end_time: new Date(now + 6 * HOUR).toISOString(),
    end_time: new Date(now + 8 * HOUR).toISOString(),
    state: 'SCHEDULED',
    create_tx_hash: null, settle_tx_hash: null,
    side_up_total_raw: '0', side_down_total_raw: '0',
    start_price: null, result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '0', side_down_total: '0', base_token_decimals: 18,
  },
  // CTS-SOL: 3 cycles (1 settled, 1 active, 1 scheduled)
  {
    id: 14, chain_id: 11155111, market_id: 'CTS-SOL', cycle_id: 0,
    start_time: new Date(now - 30 * HOUR).toISOString(),
    betting_end_time: new Date(now - 28 * HOUR).toISOString(),
    end_time: new Date(now - 26 * HOUR).toISOString(),
    state: 'SETTLED',
    create_tx_hash: txHash(0xf001), settle_tx_hash: txHash(0xf002),
    side_up_total_raw: '8000000000000000000000', side_down_total_raw: '5000000000000000000000',
    start_price: '185000000000000000000', result: 1, final_price: '210000000000000000000',
    prize_amount: '12350000000000000000000', resolved_at: new Date(now - 26 * HOUR).toISOString(),
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '8000', side_down_total: '5000', base_token_decimals: 18,
  },
  {
    id: 15, chain_id: 11155111, market_id: 'CTS-SOL', cycle_id: 1,
    start_time: new Date(now - 2 * HOUR).toISOString(),
    betting_end_time: new Date(now + 1.5 * HOUR).toISOString(),
    end_time: new Date(now + 3.5 * HOUR).toISOString(),
    state: 'ACTIVE',
    create_tx_hash: txHash(0xf101), settle_tx_hash: null,
    side_up_total_raw: '6000000000000000000000', side_down_total_raw: '4500000000000000000000',
    start_price: '195000000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '6000', side_down_total: '4500', base_token_decimals: 18,
  },
  {
    id: 16, chain_id: 11155111, market_id: 'CTS-SOL', cycle_id: 2,
    start_time: new Date(now + 3.5 * HOUR).toISOString(),
    betting_end_time: new Date(now + 6.5 * HOUR).toISOString(),
    end_time: new Date(now + 8.5 * HOUR).toISOString(),
    state: 'SCHEDULED',
    create_tx_hash: null, settle_tx_hash: null,
    side_up_total_raw: '0', side_down_total_raw: '0',
    start_price: null, result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '0', side_down_total: '0', base_token_decimals: 18,
  },
  // CTS-DOGE: 3 cycles (1 settled, 1 betting_ended, 1 active)
  {
    id: 17, chain_id: 11155111, market_id: 'CTS-DOGE', cycle_id: 0,
    start_time: new Date(now - 42 * HOUR).toISOString(),
    betting_end_time: new Date(now - 40 * HOUR).toISOString(),
    end_time: new Date(now - 38 * HOUR).toISOString(),
    state: 'SETTLED',
    create_tx_hash: txHash(0xf201), settle_tx_hash: txHash(0xf202),
    side_up_total_raw: '10000000000000000000000', side_down_total_raw: '15000000000000000000000',
    start_price: '420000000000000000', result: 2, final_price: '380000000000000000',
    prize_amount: '23750000000000000000000', resolved_at: new Date(now - 38 * HOUR).toISOString(),
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '10000', side_down_total: '15000', base_token_decimals: 18,
  },
  {
    id: 18, chain_id: 11155111, market_id: 'CTS-DOGE', cycle_id: 1,
    start_time: new Date(now - 14 * HOUR).toISOString(),
    betting_end_time: new Date(now - 5 * HOUR).toISOString(),
    end_time: new Date(now + 0.5 * HOUR).toISOString(),
    state: 'BETTING_ENDED',
    create_tx_hash: txHash(0xf301), settle_tx_hash: null,
    side_up_total_raw: '7000000000000000000000', side_down_total_raw: '9000000000000000000000',
    start_price: '450000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '7000', side_down_total: '9000', base_token_decimals: 18,
  },
  {
    id: 19, chain_id: 11155111, market_id: 'CTS-DOGE', cycle_id: 2,
    start_time: new Date(now - 1.5 * HOUR).toISOString(),
    betting_end_time: new Date(now + 2.5 * HOUR).toISOString(),
    end_time: new Date(now + 4.5 * HOUR).toISOString(),
    state: 'ACTIVE',
    create_tx_hash: txHash(0xf401), settle_tx_hash: null,
    side_up_total_raw: '5000000000000000000000', side_down_total_raw: '6500000000000000000000',
    start_price: '480000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '5000', side_down_total: '6500', base_token_decimals: 18,
  },
  // CTS-AVAX: 2 cycles (1 settled, 1 active)
  {
    id: 20, chain_id: 11155111, market_id: 'CTS-AVAX', cycle_id: 0,
    start_time: new Date(now - 20 * HOUR).toISOString(),
    betting_end_time: new Date(now - 18 * HOUR).toISOString(),
    end_time: new Date(now - 16 * HOUR).toISOString(),
    state: 'SETTLED',
    create_tx_hash: txHash(0xf501), settle_tx_hash: txHash(0xf502),
    side_up_total_raw: '4000000000000000000000', side_down_total_raw: '6000000000000000000000',
    start_price: '48000000000000000000', result: 2, final_price: '45500000000000000000',
    prize_amount: '9500000000000000000000', resolved_at: new Date(now - 16 * HOUR).toISOString(),
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '4000', side_down_total: '6000', base_token_decimals: 18,
  },
  {
    id: 21, chain_id: 11155111, market_id: 'CTS-AVAX', cycle_id: 1,
    start_time: new Date(now - 3 * HOUR).toISOString(),
    betting_end_time: new Date(now + 1 * HOUR).toISOString(),
    end_time: new Date(now + 3 * HOUR).toISOString(),
    state: 'ACTIVE',
    create_tx_hash: txHash(0xf601), settle_tx_hash: null,
    side_up_total_raw: '3500000000000000000000', side_down_total_raw: '4000000000000000000000',
    start_price: '52000000000000000000', result: null, final_price: null, prize_amount: null, resolved_at: null,
    shares_up_total_raw: '0', shares_down_total_raw: '0',
    shares_up_total: '0', shares_down_total: '0',
    side_up_total: '3500', side_down_total: '4000', base_token_decimals: 18,
  },
];

// ============================================================================
// ORDERS — across all 6 markets
// ============================================================================

export const MOCK_API_ORDERS: ApiOrder[] = [
  // Cycle 3 (CTS-BTC active) — 6 orders from different users
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 3, order_id: 10, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '5000000000000000000000', share_raw: '5500000000000000000000', from_locker: true, tx_hash: txHash(0xa010), block_number: 10420100, created_at: new Date(now - 50 * 60000).toISOString(), amount: '5000000000000000000000', share: '5500000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 3, order_id: 15, user_address: MOCK_USERS[0], side: 'DOWN', amount_raw: '3000000000000000000000', share_raw: '3300000000000000000000', from_locker: false, tx_hash: txHash(0xa015), block_number: 10420150, created_at: new Date(now - 25 * 60000).toISOString(), amount: '3000000000000000000000', share: '3300000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 3, order_id: 11, user_address: MOCK_USERS[1], side: 'DOWN', amount_raw: '3000000000000000000000', share_raw: '3200000000000000000000', from_locker: false, tx_hash: txHash(0xa011), block_number: 10420110, created_at: new Date(now - 45 * 60000).toISOString(), amount: '3000000000000000000000', share: '3200000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 3, order_id: 12, user_address: MOCK_USERS[2], side: 'UP', amount_raw: '2000000000000000000000', share_raw: '2100000000000000000000', from_locker: true, tx_hash: txHash(0xa012), block_number: 10420120, created_at: new Date(now - 40 * 60000).toISOString(), amount: '2000000000000000000000', share: '2100000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 3, order_id: 13, user_address: MOCK_USERS[3], side: 'DOWN', amount_raw: '4500000000000000000000', share_raw: '4800000000000000000000', from_locker: false, tx_hash: txHash(0xa013), block_number: 10420130, created_at: new Date(now - 35 * 60000).toISOString(), amount: '4500000000000000000000', share: '4800000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 3, order_id: 14, user_address: MOCK_USERS[4], side: 'UP', amount_raw: '8000000000000000000000', share_raw: '8800000000000000000000', from_locker: true, tx_hash: txHash(0xa014), block_number: 10420140, created_at: new Date(now - 30 * 60000).toISOString(), amount: '8000000000000000000000', share: '8800000000000000000000', base_token_decimals: 18 },
  // Cycle 2 (CTS-BTC betting ended) — 3 orders
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 2, order_id: 7, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '10000000000000000000000', share_raw: '11000000000000000000000', from_locker: true, tx_hash: txHash(0xa007), block_number: 10419050, created_at: new Date(now - 6 * HOUR).toISOString(), amount: '10000000000000000000000', share: '11000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 2, order_id: 8, user_address: MOCK_USERS[1], side: 'DOWN', amount_raw: '15000000000000000000000', share_raw: '16000000000000000000000', from_locker: false, tx_hash: txHash(0xa008), block_number: 10419060, created_at: new Date(now - 5.5 * HOUR).toISOString(), amount: '15000000000000000000000', share: '16000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 2, order_id: 9, user_address: MOCK_USERS[2], side: 'UP', amount_raw: '20000000000000000000000', share_raw: '22000000000000000000000', from_locker: true, tx_hash: txHash(0xa009), block_number: 10419070, created_at: new Date(now - 5 * HOUR).toISOString(), amount: '20000000000000000000000', share: '22000000000000000000000', base_token_decimals: 18 },
  // Cycle 1 (CTS-BTC settled) — 4 orders
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 1, order_id: 1, user_address: MOCK_USERS[0], side: 'DOWN', amount_raw: '2000000000000000000000', share_raw: '2000000000000000000000', from_locker: true, tx_hash: txHash(0xa001), block_number: 10417000, created_at: new Date(now - 23 * HOUR).toISOString(), amount: '2000000000000000000000', share: '2000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 1, order_id: 2, user_address: MOCK_USERS[1], side: 'UP', amount_raw: '1000000000000000000000', share_raw: '1000000000000000000000', from_locker: true, tx_hash: txHash(0xa002), block_number: 10417010, created_at: new Date(now - 22.5 * HOUR).toISOString(), amount: '1000000000000000000000', share: '1000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 1, order_id: 3, user_address: MOCK_USERS[2], side: 'DOWN', amount_raw: '6500000000000000000000', share_raw: '7000000000000000000000', from_locker: false, tx_hash: txHash(0xa003), block_number: 10417020, created_at: new Date(now - 22 * HOUR).toISOString(), amount: '6500000000000000000000', share: '7000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', cycle_id: 1, order_id: 4, user_address: MOCK_USERS[3], side: 'DOWN', amount_raw: '3500000000000000000000', share_raw: '3500000000000000000000', from_locker: false, tx_hash: txHash(0xa004), block_number: 10417030, created_at: new Date(now - 21.5 * HOUR).toISOString(), amount: '3500000000000000000000', share: '3500000000000000000000', base_token_decimals: 18 },
  // CTS-ETH settled cycle 0 — 4 orders (added MOCK_USERS[0] for portfolio closed predictions)
  { chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 0, order_id: 199, user_address: MOCK_USERS[0], side: 'DOWN', amount_raw: '2500000000000000000000', share_raw: '2500000000000000000000', from_locker: false, tx_hash: txHash(0xb199), block_number: 10414990, created_at: new Date(now - 35.5 * HOUR).toISOString(), amount: '2500000000000000000000', share: '2500000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 0, order_id: 200, user_address: MOCK_USERS[1], side: 'DOWN', amount_raw: '3000000000000000000000', share_raw: '3000000000000000000000', from_locker: false, tx_hash: txHash(0xb200), block_number: 10415000, created_at: new Date(now - 35 * HOUR).toISOString(), amount: '3000000000000000000000', share: '3000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 0, order_id: 201, user_address: MOCK_USERS[2], side: 'UP', amount_raw: '5000000000000000000000', share_raw: '5000000000000000000000', from_locker: true, tx_hash: txHash(0xb201), block_number: 10415010, created_at: new Date(now - 34.5 * HOUR).toISOString(), amount: '5000000000000000000000', share: '5000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 0, order_id: 202, user_address: MOCK_USERS[4], side: 'DOWN', amount_raw: '4000000000000000000000', share_raw: '4000000000000000000000', from_locker: false, tx_hash: txHash(0xb202), block_number: 10415020, created_at: new Date(now - 34 * HOUR).toISOString(), amount: '4000000000000000000000', share: '4000000000000000000000', base_token_decimals: 18 },
  // CTS-ETH betting_ended cycle 1 — 3 orders
  { chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 1, order_id: 20, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '4000000000000000000000', share_raw: '4400000000000000000000', from_locker: true, tx_hash: txHash(0xb020), block_number: 10420200, created_at: new Date(now - 8 * HOUR).toISOString(), amount: '4000000000000000000000', share: '4400000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 1, order_id: 21, user_address: MOCK_USERS[1], side: 'DOWN', amount_raw: '6000000000000000000000', share_raw: '6200000000000000000000', from_locker: false, tx_hash: txHash(0xb021), block_number: 10420210, created_at: new Date(now - 7 * HOUR).toISOString(), amount: '6000000000000000000000', share: '6200000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 1, order_id: 22, user_address: MOCK_USERS[4], side: 'UP', amount_raw: '5000000000000000000000', share_raw: '5500000000000000000000', from_locker: true, tx_hash: txHash(0xb022), block_number: 10420220, created_at: new Date(now - 6 * HOUR).toISOString(), amount: '5000000000000000000000', share: '5500000000000000000000', base_token_decimals: 18 },
  // CTS-ETH active cycle 2 — 2 orders
  { chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 2, order_id: 23, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '3000000000000000000000', share_raw: '3000000000000000000000', from_locker: true, tx_hash: txHash(0xb023), block_number: 10421000, created_at: new Date(now - 45 * 60000).toISOString(), amount: '3000000000000000000000', share: '3000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-ETH', cycle_id: 2, order_id: 24, user_address: MOCK_USERS[3], side: 'DOWN', amount_raw: '2500000000000000000000', share_raw: '2500000000000000000000', from_locker: false, tx_hash: txHash(0xb024), block_number: 10421010, created_at: new Date(now - 30 * 60000).toISOString(), amount: '2500000000000000000000', share: '2500000000000000000000', base_token_decimals: 18 },
  // CTS-BNB settled cycle 0 — 3 orders
  { chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 0, order_id: 300, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '3000000000000000000000', share_raw: '3000000000000000000000', from_locker: true, tx_hash: txHash(0xe300), block_number: 10431000, created_at: new Date(now - 39 * HOUR).toISOString(), amount: '3000000000000000000000', share: '3000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 0, order_id: 301, user_address: MOCK_USERS[3], side: 'DOWN', amount_raw: '4500000000000000000000', share_raw: '4500000000000000000000', from_locker: false, tx_hash: txHash(0xe301), block_number: 10431010, created_at: new Date(now - 38.5 * HOUR).toISOString(), amount: '4500000000000000000000', share: '4500000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 0, order_id: 302, user_address: MOCK_USERS[4], side: 'UP', amount_raw: '3000000000000000000000', share_raw: '3000000000000000000000', from_locker: true, tx_hash: txHash(0xe302), block_number: 10431020, created_at: new Date(now - 38 * HOUR).toISOString(), amount: '3000000000000000000000', share: '3000000000000000000000', base_token_decimals: 18 },
  // CTS-BNB active cycle 2 — 3 orders
  { chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 2, order_id: 30, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '2000000000000000000000', share_raw: '2000000000000000000000', from_locker: true, tx_hash: txHash(0xe030), block_number: 10432000, created_at: new Date(now - 90 * 60000).toISOString(), amount: '2000000000000000000000', share: '2000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 2, order_id: 31, user_address: MOCK_USERS[2], side: 'DOWN', amount_raw: '1500000000000000000000', share_raw: '1500000000000000000000', from_locker: false, tx_hash: txHash(0xe031), block_number: 10432010, created_at: new Date(now - 80 * 60000).toISOString(), amount: '1500000000000000000000', share: '1500000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BNB', cycle_id: 2, order_id: 32, user_address: MOCK_USERS[4], side: 'UP', amount_raw: '2000000000000000000000', share_raw: '2000000000000000000000', from_locker: false, tx_hash: txHash(0xe032), block_number: 10432020, created_at: new Date(now - 70 * 60000).toISOString(), amount: '2000000000000000000000', share: '2000000000000000000000', base_token_decimals: 18 },
  // CTS-SOL settled cycle 0 — 3 orders
  { chain_id: 11155111, market_id: 'CTS-SOL', cycle_id: 0, order_id: 400, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '4000000000000000000000', share_raw: '4000000000000000000000', from_locker: true, tx_hash: txHash(0xf010), block_number: 10441000, created_at: new Date(now - 29 * HOUR).toISOString(), amount: '4000000000000000000000', share: '4000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-SOL', cycle_id: 0, order_id: 401, user_address: MOCK_USERS[2], side: 'DOWN', amount_raw: '5000000000000000000000', share_raw: '5000000000000000000000', from_locker: false, tx_hash: txHash(0xf011), block_number: 10441010, created_at: new Date(now - 28.5 * HOUR).toISOString(), amount: '5000000000000000000000', share: '5000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-SOL', cycle_id: 0, order_id: 402, user_address: MOCK_USERS[4], side: 'UP', amount_raw: '4000000000000000000000', share_raw: '4000000000000000000000', from_locker: true, tx_hash: txHash(0xf012), block_number: 10441020, created_at: new Date(now - 28 * HOUR).toISOString(), amount: '4000000000000000000000', share: '4000000000000000000000', base_token_decimals: 18 },
  // CTS-SOL active cycle 1 — 3 orders
  { chain_id: 11155111, market_id: 'CTS-SOL', cycle_id: 1, order_id: 410, user_address: MOCK_USERS[1], side: 'UP', amount_raw: '3000000000000000000000', share_raw: '3200000000000000000000', from_locker: true, tx_hash: txHash(0xf020), block_number: 10442000, created_at: new Date(now - 100 * 60000).toISOString(), amount: '3000000000000000000000', share: '3200000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-SOL', cycle_id: 1, order_id: 411, user_address: MOCK_USERS[3], side: 'DOWN', amount_raw: '4500000000000000000000', share_raw: '4500000000000000000000', from_locker: false, tx_hash: txHash(0xf021), block_number: 10442010, created_at: new Date(now - 90 * 60000).toISOString(), amount: '4500000000000000000000', share: '4500000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-SOL', cycle_id: 1, order_id: 412, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '3000000000000000000000', share_raw: '3000000000000000000000', from_locker: false, tx_hash: txHash(0xf022), block_number: 10442020, created_at: new Date(now - 80 * 60000).toISOString(), amount: '3000000000000000000000', share: '3000000000000000000000', base_token_decimals: 18 },
  // CTS-DOGE settled cycle 0 — 3 orders
  { chain_id: 11155111, market_id: 'CTS-DOGE', cycle_id: 0, order_id: 500, user_address: MOCK_USERS[1], side: 'UP', amount_raw: '5000000000000000000000', share_raw: '5000000000000000000000', from_locker: false, tx_hash: txHash(0xf110), block_number: 10451000, created_at: new Date(now - 41 * HOUR).toISOString(), amount: '5000000000000000000000', share: '5000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-DOGE', cycle_id: 0, order_id: 501, user_address: MOCK_USERS[3], side: 'DOWN', amount_raw: '8000000000000000000000', share_raw: '8500000000000000000000', from_locker: true, tx_hash: txHash(0xf111), block_number: 10451010, created_at: new Date(now - 40.5 * HOUR).toISOString(), amount: '8000000000000000000000', share: '8500000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-DOGE', cycle_id: 0, order_id: 502, user_address: MOCK_USERS[4], side: 'DOWN', amount_raw: '7000000000000000000000', share_raw: '7000000000000000000000', from_locker: false, tx_hash: txHash(0xf112), block_number: 10451020, created_at: new Date(now - 40 * HOUR).toISOString(), amount: '7000000000000000000000', share: '7000000000000000000000', base_token_decimals: 18 },
  // CTS-DOGE active cycle 2 — 3 orders
  { chain_id: 11155111, market_id: 'CTS-DOGE', cycle_id: 2, order_id: 510, user_address: MOCK_USERS[0], side: 'DOWN', amount_raw: '3000000000000000000000', share_raw: '3200000000000000000000', from_locker: true, tx_hash: txHash(0xf120), block_number: 10452000, created_at: new Date(now - 80 * 60000).toISOString(), amount: '3000000000000000000000', share: '3200000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-DOGE', cycle_id: 2, order_id: 511, user_address: MOCK_USERS[2], side: 'UP', amount_raw: '5000000000000000000000', share_raw: '5000000000000000000000', from_locker: false, tx_hash: txHash(0xf121), block_number: 10452010, created_at: new Date(now - 70 * 60000).toISOString(), amount: '5000000000000000000000', share: '5000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-DOGE', cycle_id: 2, order_id: 512, user_address: MOCK_USERS[4], side: 'DOWN', amount_raw: '3500000000000000000000', share_raw: '3500000000000000000000', from_locker: true, tx_hash: txHash(0xf122), block_number: 10452020, created_at: new Date(now - 60 * 60000).toISOString(), amount: '3500000000000000000000', share: '3500000000000000000000', base_token_decimals: 18 },
  // CTS-AVAX settled cycle 0 — 3 orders
  { chain_id: 11155111, market_id: 'CTS-AVAX', cycle_id: 0, order_id: 600, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '2000000000000000000000', share_raw: '2000000000000000000000', from_locker: true, tx_hash: txHash(0xf210), block_number: 10461000, created_at: new Date(now - 19 * HOUR).toISOString(), amount: '2000000000000000000000', share: '2000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-AVAX', cycle_id: 0, order_id: 601, user_address: MOCK_USERS[1], side: 'DOWN', amount_raw: '3500000000000000000000', share_raw: '3500000000000000000000', from_locker: false, tx_hash: txHash(0xf211), block_number: 10461010, created_at: new Date(now - 18.5 * HOUR).toISOString(), amount: '3500000000000000000000', share: '3500000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-AVAX', cycle_id: 0, order_id: 602, user_address: MOCK_USERS[3], side: 'DOWN', amount_raw: '2500000000000000000000', share_raw: '2500000000000000000000', from_locker: true, tx_hash: txHash(0xf212), block_number: 10461020, created_at: new Date(now - 18 * HOUR).toISOString(), amount: '2500000000000000000000', share: '2500000000000000000000', base_token_decimals: 18 },
  // CTS-AVAX active cycle 1 — 3 orders
  { chain_id: 11155111, market_id: 'CTS-AVAX', cycle_id: 1, order_id: 610, user_address: MOCK_USERS[2], side: 'UP', amount_raw: '2000000000000000000000', share_raw: '2100000000000000000000', from_locker: false, tx_hash: txHash(0xf220), block_number: 10462000, created_at: new Date(now - 150 * 60000).toISOString(), amount: '2000000000000000000000', share: '2100000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-AVAX', cycle_id: 1, order_id: 611, user_address: MOCK_USERS[4], side: 'DOWN', amount_raw: '2500000000000000000000', share_raw: '2500000000000000000000', from_locker: true, tx_hash: txHash(0xf221), block_number: 10462010, created_at: new Date(now - 140 * 60000).toISOString(), amount: '2500000000000000000000', share: '2500000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-AVAX', cycle_id: 1, order_id: 612, user_address: MOCK_USERS[0], side: 'UP', amount_raw: '1500000000000000000000', share_raw: '1500000000000000000000', from_locker: true, tx_hash: txHash(0xf222), block_number: 10462020, created_at: new Date(now - 130 * 60000).toISOString(), amount: '1500000000000000000000', share: '1500000000000000000000', base_token_decimals: 18 },
];

// ============================================================================
// ORDER SETTLEMENTS — for settled cycles
// ============================================================================

export const MOCK_API_ORDER_SETTLEMENTS: ApiOrderSettlement[] = [
  // CTS-BTC cycle 1 (result=2, DOWN wins)
  { chain_id: 11155111, market_id: 'CTS-BTC', order_id: 1, user_address: MOCK_USERS[0], reward_raw: '3800000000000000000000', tx_hash: txHash(0xc001), block_number: 10418000, settled_at: new Date(now - 20 * HOUR).toISOString(), reward: '3800000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', order_id: 2, user_address: MOCK_USERS[1], reward_raw: '0', tx_hash: txHash(0xc002), block_number: 10418000, settled_at: new Date(now - 20 * HOUR).toISOString(), reward: '0', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', order_id: 3, user_address: MOCK_USERS[2], reward_raw: '12500000000000000000000', tx_hash: txHash(0xc003), block_number: 10418000, settled_at: new Date(now - 20 * HOUR).toISOString(), reward: '12500000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', order_id: 4, user_address: MOCK_USERS[3], reward_raw: '6650000000000000000000', tx_hash: txHash(0xc004), block_number: 10418000, settled_at: new Date(now - 20 * HOUR).toISOString(), reward: '6650000000000000000000', base_token_decimals: 18 },
  // CTS-BTC cycle 0 (result=1, UP wins)
  { chain_id: 11155111, market_id: 'CTS-BTC', order_id: 100, user_address: MOCK_USERS[0], reward_raw: '25000000000000000000000', tx_hash: txHash(0xc100), block_number: 10416000, settled_at: new Date(now - 44 * HOUR).toISOString(), reward: '25000000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BTC', order_id: 101, user_address: MOCK_USERS[3], reward_raw: '0', tx_hash: txHash(0xc101), block_number: 10416000, settled_at: new Date(now - 44 * HOUR).toISOString(), reward: '0', base_token_decimals: 18 },
  // CTS-ETH cycle 0 (result=2, DOWN wins)
  { chain_id: 11155111, market_id: 'CTS-ETH', order_id: 199, user_address: MOCK_USERS[0], reward_raw: '4100000000000000000000', tx_hash: txHash(0xc199), block_number: 10418500, settled_at: new Date(now - 32 * HOUR).toISOString(), reward: '4100000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-ETH', order_id: 200, user_address: MOCK_USERS[1], reward_raw: '4900000000000000000000', tx_hash: txHash(0xc200), block_number: 10418500, settled_at: new Date(now - 32 * HOUR).toISOString(), reward: '4900000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-ETH', order_id: 201, user_address: MOCK_USERS[2], reward_raw: '0', tx_hash: txHash(0xc201), block_number: 10418500, settled_at: new Date(now - 32 * HOUR).toISOString(), reward: '0', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-ETH', order_id: 202, user_address: MOCK_USERS[4], reward_raw: '6500000000000000000000', tx_hash: txHash(0xc202), block_number: 10418500, settled_at: new Date(now - 32 * HOUR).toISOString(), reward: '6500000000000000000000', base_token_decimals: 18 },
  // CTS-BNB cycle 0 (result=1, UP wins)
  { chain_id: 11155111, market_id: 'CTS-BNB', order_id: 300, user_address: MOCK_USERS[0], reward_raw: '4975000000000000000000', tx_hash: txHash(0xc300), block_number: 10432500, settled_at: new Date(now - 36 * HOUR).toISOString(), reward: '4975000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BNB', order_id: 301, user_address: MOCK_USERS[3], reward_raw: '0', tx_hash: txHash(0xc301), block_number: 10432500, settled_at: new Date(now - 36 * HOUR).toISOString(), reward: '0', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-BNB', order_id: 302, user_address: MOCK_USERS[4], reward_raw: '5000000000000000000000', tx_hash: txHash(0xc302), block_number: 10432500, settled_at: new Date(now - 36 * HOUR).toISOString(), reward: '5000000000000000000000', base_token_decimals: 18 },
  // CTS-SOL cycle 0 (result=1, UP wins)
  { chain_id: 11155111, market_id: 'CTS-SOL', order_id: 400, user_address: MOCK_USERS[0], reward_raw: '6200000000000000000000', tx_hash: txHash(0xc400), block_number: 10442500, settled_at: new Date(now - 26 * HOUR).toISOString(), reward: '6200000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-SOL', order_id: 401, user_address: MOCK_USERS[2], reward_raw: '0', tx_hash: txHash(0xc401), block_number: 10442500, settled_at: new Date(now - 26 * HOUR).toISOString(), reward: '0', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-SOL', order_id: 402, user_address: MOCK_USERS[4], reward_raw: '6150000000000000000000', tx_hash: txHash(0xc402), block_number: 10442500, settled_at: new Date(now - 26 * HOUR).toISOString(), reward: '6150000000000000000000', base_token_decimals: 18 },
  // CTS-DOGE cycle 0 (result=2, DOWN wins)
  { chain_id: 11155111, market_id: 'CTS-DOGE', order_id: 500, user_address: MOCK_USERS[1], reward_raw: '0', tx_hash: txHash(0xc500), block_number: 10452500, settled_at: new Date(now - 38 * HOUR).toISOString(), reward: '0', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-DOGE', order_id: 501, user_address: MOCK_USERS[3], reward_raw: '12700000000000000000000', tx_hash: txHash(0xc501), block_number: 10452500, settled_at: new Date(now - 38 * HOUR).toISOString(), reward: '12700000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-DOGE', order_id: 502, user_address: MOCK_USERS[4], reward_raw: '11050000000000000000000', tx_hash: txHash(0xc502), block_number: 10452500, settled_at: new Date(now - 38 * HOUR).toISOString(), reward: '11050000000000000000000', base_token_decimals: 18 },
  // CTS-AVAX cycle 0 (result=2, DOWN wins)
  { chain_id: 11155111, market_id: 'CTS-AVAX', order_id: 600, user_address: MOCK_USERS[0], reward_raw: '0', tx_hash: txHash(0xc600), block_number: 10462500, settled_at: new Date(now - 16 * HOUR).toISOString(), reward: '0', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-AVAX', order_id: 601, user_address: MOCK_USERS[1], reward_raw: '5550000000000000000000', tx_hash: txHash(0xc601), block_number: 10462500, settled_at: new Date(now - 16 * HOUR).toISOString(), reward: '5550000000000000000000', base_token_decimals: 18 },
  { chain_id: 11155111, market_id: 'CTS-AVAX', order_id: 602, user_address: MOCK_USERS[3], reward_raw: '3950000000000000000000', tx_hash: txHash(0xc602), block_number: 10462500, settled_at: new Date(now - 16 * HOUR).toISOString(), reward: '3950000000000000000000', base_token_decimals: 18 },
];

// ============================================================================
// LOCKER
// ============================================================================

export const MOCK_API_LOCKER: ApiLocker = {
  chain_id: 11155111,
  locker_id: 'CTS',
  locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address,
  start_block: 10125494,
  base_token_decimals: 18,
  status: 'active',
};

// ============================================================================
// LOCKER ORDERS — lock records from different users
// ============================================================================

// NOTE: Backend pre-divides locker order amounts by 10^18 (see docs/backend-gaps.md).
// Mock amounts here are human-readable values, not raw wei.
export const MOCK_API_LOCKER_ORDERS: ApiLockerOrder[] = [
  { id: 1, chain_id: 11155111, locker_id: 'CTS', locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address, user_address: MOCK_USERS[0], amount: '5000', lock_start_time: new Date(now - 5 * DAY).toISOString(), lock_end_time: new Date(now + 2 * DAY).toISOString(), tx_hash: txHash(0xd001), block_number: 10130000, created_at: new Date(now - 5 * DAY).toISOString() },
  { id: 2, chain_id: 11155111, locker_id: 'CTS', locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address, user_address: MOCK_USERS[1], amount: '10000', lock_start_time: new Date(now - 4 * DAY).toISOString(), lock_end_time: new Date(now + 3 * DAY).toISOString(), tx_hash: txHash(0xd002), block_number: 10131000, created_at: new Date(now - 4 * DAY).toISOString() },
  { id: 3, chain_id: 11155111, locker_id: 'CTS', locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address, user_address: MOCK_USERS[2], amount: '3000', lock_start_time: new Date(now - 3 * DAY).toISOString(), lock_end_time: new Date(now + 4 * DAY).toISOString(), tx_hash: txHash(0xd003), block_number: 10132000, created_at: new Date(now - 3 * DAY).toISOString() },
  { id: 4, chain_id: 11155111, locker_id: 'CTS', locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address, user_address: MOCK_USERS[0], amount: '2000', lock_start_time: new Date(now - 1 * DAY).toISOString(), lock_end_time: new Date(now + 6 * DAY).toISOString(), tx_hash: txHash(0xd004), block_number: 10135000, created_at: new Date(now - 1 * DAY).toISOString() },
  { id: 9, chain_id: 11155111, locker_id: 'CTS', locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address, user_address: MOCK_USERS[0], amount: '8000', lock_start_time: new Date(now - 3 * DAY).toISOString(), lock_end_time: new Date(now + 4 * DAY).toISOString(), tx_hash: txHash(0xd009), block_number: 10133000, created_at: new Date(now - 3 * DAY).toISOString() },
  { id: 5, chain_id: 11155111, locker_id: 'CTS', locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address, user_address: MOCK_USERS[4], amount: '15000', lock_start_time: new Date(now - 6 * DAY).toISOString(), lock_end_time: new Date(now + 1 * DAY).toISOString(), tx_hash: txHash(0xd005), block_number: 10129000, created_at: new Date(now - 6 * DAY).toISOString() },
  { id: 6, chain_id: 11155111, locker_id: 'CTS', locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address, user_address: MOCK_USERS[3], amount: '8000', lock_start_time: new Date(now - 10 * DAY).toISOString(), lock_end_time: new Date(now - 3 * DAY).toISOString(), tx_hash: txHash(0xd006), block_number: 10126000, created_at: new Date(now - 10 * DAY).toISOString() },
  { id: 7, chain_id: 11155111, locker_id: 'CTS', locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address, user_address: MOCK_USERS[1], amount: '12000', lock_start_time: new Date(now - 2 * DAY).toISOString(), lock_end_time: new Date(now + 5 * DAY).toISOString(), tx_hash: txHash(0xd007), block_number: 10136000, created_at: new Date(now - 2 * DAY).toISOString() },
  { id: 8, chain_id: 11155111, locker_id: 'CTS', locker_contract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' as Address, user_address: MOCK_USERS[3], amount: '6000', lock_start_time: new Date(now - 14 * DAY).toISOString(), lock_end_time: new Date(now - 7 * DAY).toISOString(), tx_hash: txHash(0xd008), block_number: 10122000, created_at: new Date(now - 14 * DAY).toISOString() },
];

// ============================================================================
// MARKET PRICES — per-market lookup (all 6 markets)
// ============================================================================

export const MOCK_API_MARKET_PRICES: Record<string, ApiMarketPrice> = {
  'CTS-BTC': {
    market_id: 'CTS-BTC',
    price: '85000000000000000000000',
    price_usd: '85000.00',
    timestamp: new Date(now).toISOString(),
    change_24h: 2.3,
  },
  'CTS-ETH': {
    market_id: 'CTS-ETH',
    price: '3500000000000000000000',
    price_usd: '3500.00',
    timestamp: new Date(now).toISOString(),
    change_24h: -1.2,
  },
  'CTS-BNB': {
    market_id: 'CTS-BNB',
    price: '620000000000000000000',
    price_usd: '620.00',
    timestamp: new Date(now).toISOString(),
    change_24h: 0.8,
  },
  'CTS-SOL': {
    market_id: 'CTS-SOL',
    price: '198000000000000000000',
    price_usd: '198.00',
    timestamp: new Date(now).toISOString(),
    change_24h: 3.5,
  },
  'CTS-DOGE': {
    market_id: 'CTS-DOGE',
    price: '470000000000000000',
    price_usd: '0.47',
    timestamp: new Date(now).toISOString(),
    change_24h: -2.8,
  },
  'CTS-AVAX': {
    market_id: 'CTS-AVAX',
    price: '52000000000000000000',
    price_usd: '52.00',
    timestamp: new Date(now).toISOString(),
    change_24h: 1.1,
  },
};

/** @deprecated Use MOCK_API_MARKET_PRICES instead */
export const MOCK_API_MARKET_PRICE: ApiMarketPrice = MOCK_API_MARKET_PRICES['CTS-BTC'];

// ============================================================================
// SIGNATURE RESPONSES
// ============================================================================

export const MOCK_BET_SIGNATURE_RESPONSE: BetSignatureResponse = {
  message_hash: '0xabcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890',
  signer: '0xa742fffc3d5d8e527e83d2f4a1a5739cba828503' as Address,
  signature: '0x' + 'ab'.repeat(32) + 'cd'.repeat(32) + '1b',
};

const futureDeadline = String(Math.floor(Date.now() / 1000) + 7200);

export const MOCK_TRANSFER_MESSAGE_HASH_RESPONSE: TransferMessageHashResponse = {
  typed_data: {
    types: {
      EIP712Domain: [
        { name: 'name', type: 'string' }, { name: 'version', type: 'string' },
        { name: 'chainId', type: 'uint256' }, { name: 'verifyingContract', type: 'address' },
      ],
      TransferToPredict: [
        { name: 'user', type: 'address' }, { name: 'predictMarket', type: 'address' },
        { name: 'amount', type: 'uint256' }, { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' },
      ],
    },
    primaryType: 'TransferToPredict',
    domain: { name: 'TokenLocker', version: '1', chainId: 11155111, verifyingContract: '0xcdBe5dF736fa0e576fDCf4cEcd6A2A66ACe1E277' },
    message: { user: MOCK_USERS[0], predictMarket: '0x68f966e387610d1d2b5a9a84806219f3e0b9155f', amount: '5000000000000000000000', nonce: '0', deadline: futureDeadline },
  },
  message_hash: '0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef',
};

// ============================================================================
// TVL DATA
// ============================================================================

export const MOCK_TVL_DATA: ApiTvl = {
  total: '$920,218.02',
  trustlocker: '$412,105.50',
  rollocker: '$298,012.32',
  prediction: '$210,100.20',
};

// ============================================================================
// TVL HISTORY — deterministic PRNG chart data
// ============================================================================

function generateMockTvlHistory(tab: string): ApiTvlHistory[] {
  const data: ApiTvlHistory[] = [];
  const startDate = new Date(2023, 9, 1);
  const endDate = new Date(2025, 9, 1);
  const seeds: Record<string, { start: number; decay: number }> = {
    total: { start: 150_000, decay: 0.05 },
    trustlocker: { start: 80_000, decay: 0.08 },
    rollocker: { start: 60_000, decay: 0.06 },
    prediction: { start: 40_000, decay: 0.1 },
  };
  const seed = seeds[tab] ?? seeds['total'];
  let rng = tab.length * 12345;
  const pseudoRandom = (): number => { rng = (rng * 16807 + 0) % 2147483647; return rng / 2147483647; };
  let value = seed.start;
  const current = new Date(startDate);
  while (current <= endDate) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    data.push({
      date: `${year}-${month}-${day}`,
      total: tab === 'total' ? value : 0,
      trustlocker: tab === 'trustlocker' ? value : 0,
      rollocker: tab === 'rollocker' ? value : 0,
      prediction: tab === 'prediction' ? value : 0,
    });
    const noise = (pseudoRandom() - 0.5) * value * 0.15;
    const trend = seed.start * 0.02 * (pseudoRandom() - 0.3);
    value = Math.max(5_000, value + trend + noise);
    if (value > 200_000) value = 200_000 - (pseudoRandom() * 20_000);
    current.setMonth(current.getMonth() + 1);
  }
  return data;
}

export const MOCK_TVL_HISTORY: Record<string, ApiTvlHistory[]> = {
  total: generateMockTvlHistory('total'),
  trustlocker: generateMockTvlHistory('trustlocker'),
  rollocker: generateMockTvlHistory('rollocker'),
  prediction: generateMockTvlHistory('prediction'),
};

// ============================================================================
// PRICE HISTORY — deterministic PRNG chart data for prediction detail
// ============================================================================

function generateMockPriceHistory(): ApiPriceHistory[] {
  const data: ApiPriceHistory[] = [];
  const startDate = new Date(2025, 9, 1);
  const endDate = new Date(2025, 11, 31);
  let rng = 54321;
  const pseudoRandom = (): number => { rng = (rng * 16807 + 0) % 2147483647; return rng / 2147483647; };
  let price = 92_000;
  const current = new Date(startDate);
  while (current <= endDate) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    data.push({ date: `${year}-${month}-${day}`, value: Math.round(price) });
    const drift = 50;
    const volatility = 1200;
    price += drift + (pseudoRandom() - 0.5) * volatility;
    price = Math.max(88_000, Math.min(108_000, price));
    current.setDate(current.getDate() + 1);
  }
  return data;
}

export const MOCK_PRICE_HISTORY: ApiPriceHistory[] = generateMockPriceHistory();

// ============================================================================
// TOKEN INFO — for rollock detail page
// ============================================================================

export const MOCK_TOKEN_INFO: ApiTokenInfo = {
  name: 'BNB',
  symbol: 'BNB',
  chain: 'BNB',
  total_supply: '100M',
  fdv: '$100M',
  trustlocker_amount: '$100M',
};
